import { adminReturnPath } from './quoteForm';
import type { ReactNode } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes, useSearchParams } from 'react-router-dom';
import {
  Check,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  LogOut,
  Settings,
  Users,
  Wallet,
} from 'lucide-react';
import { SignIn, SignUp, useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { clerkAppearance } from '../lib/clerkAppearance';
import { errorMessage } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from '../components/Button';
import { ThemeToggle } from '../components/ThemeToggle';
import zerothLogo from '../assets/zeroth-logo.png';
import { usePartnerDashboard, useProgram } from './usePartnerDashboard';
import { ReferralShareControls } from './ReferralShareControls';
import {
  SUPPORT_EMAIL,
  customerOffer,
  percent,
  tierFor,
  usePortalPath,
  type PortalDashboard,
} from './portalContext';
import {
  EarningsPage,
  LinksPage,
  OverviewPage,
  ReferralsPage,
  SettingsPage,
} from './PartnerPortalPages';
import { AdminPortal } from './AdminPortal';

const NAV = [
  { path: '', label: 'Overview', icon: LayoutDashboard },
  { path: 'links', label: 'Links & sharing', icon: Link2 },
  { path: 'referrals', label: 'Referrals', icon: Users },
  { path: 'earnings', label: 'Earnings & payouts', icon: Wallet },
  { path: 'settings', label: 'Settings', icon: Settings },
];

export function PartnerPortal() {
  return (
    <Routes>
      <Route path="sign-in/*" element={<PartnerAuth mode="sign-in" />} />
      <Route path="sign-up/*" element={<PartnerAuth mode="sign-up" />} />
      {/* Outside PartnerSession so staff are never enrolled as partners just by opening it. */}
      <Route path="admin/*" element={<AdminPortal />} />
      <Route element={<PartnerSession />}>
        <Route index element={<OverviewPage />} />
        <Route path="links" element={<LinksPage />} />
        <Route path="referrals" element={<ReferralsPage />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function FullScreen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-[var(--app-viewport-height)] items-center justify-center bg-background px-5 text-center text-foreground">
      <div className="max-w-sm space-y-4">{children}</div>
    </div>
  );
}

function PartnerSession() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <FullScreen><Spinner /></FullScreen>;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <PartnerShell />;
}

function Spinner() {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"
    />
  );
}

function PartnerShell() {
  const { query, join, data } = usePartnerDashboard();
  const program = useProgram();
  if (query.isError || join.isError || program.isError) {
    return (
      <FullScreen>
        <h1 className="text-xl font-semibold">We couldn’t load your partner account</h1>
        <p role="alert" className="text-sm text-muted-foreground">
          {errorMessage(join.error ?? query.error ?? program.error)} Your earnings have not changed.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            if (join.isError) join.mutate();
            if (query.isError) void query.refetch();
            if (program.isError) void program.refetch();
          }}
        >
          Try again
        </Button>
      </FullScreen>
    );
  }
  if (!data || !program.data) {
    return (
      <FullScreen>
        <Spinner />
        <p className="text-sm text-muted-foreground">
          {join.isPending ? 'Creating your referral link…' : 'Loading your partner dashboard…'}
        </p>
      </FullScreen>
    );
  }
  const dashboard = { ...data, program: program.data } as PortalDashboard;
  return (
    <div className="min-h-[var(--app-viewport-height)] bg-background text-foreground lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <Sidebar dashboard={dashboard} />
      <div className="min-w-0">
        <TopBar referralUrl={dashboard.partner.referralUrl} referralCode={dashboard.partner.referralCode} code={dashboard.partner.code} />
        <MobileNav />
        <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet context={dashboard} />
        </main>
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <img src={zerothLogo} alt="" className="h-8 w-8 object-contain" />
      <div className="leading-tight">
        <p className="text-base font-semibold tracking-tight">Zeroth</p>
        <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          Partner portal
        </p>
      </div>
    </div>
  );
}

function NavItems({ compact = false }: { compact?: boolean }) {
  const portalPath = usePortalPath();
  return (
    <>
      {NAV.map(({ path, label, icon: Icon }) => (
        <NavLink
          key={label}
          to={portalPath(path)}
          end={path === ''}
          className={({ isActive }) =>
            cn(
              'flex shrink-0 items-center gap-2.5 rounded-lg text-sm font-medium transition-colors',
              compact ? 'px-3 py-2' : 'px-3 py-2.5',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </NavLink>
      ))}
    </>
  );
}

function Sidebar({ dashboard }: { dashboard: PortalDashboard }) {
  const { signOut } = useClerk();
  const tier = tierFor(dashboard.program, dashboard.partner.rateBasisPoints);
  return (
    <aside className="sticky top-0 hidden h-[var(--app-viewport-height)] flex-col border-r border-border bg-card px-4 py-5 lg:flex">
      <Brand />
      <div className="mt-6 rounded-xl border border-border bg-background p-3.5">
        <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          Your tier
        </p>
        <div className="mt-1 flex items-center justify-between">
          <p className="text-lg font-semibold">{tier.name}</p>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
            {percent(dashboard.partner.rateBasisPoints)}
          </span>
        </div>
      </div>
      <nav aria-label="Partner portal" className="mt-5 flex flex-col gap-1">
        <NavItems />
      </nav>
      <div className="mt-auto space-y-1 border-t border-border pt-4">
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Zeroth%20partner%20program`}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <LifeBuoy className="h-4 w-4" aria-hidden="true" /> Partner support
        </a>
        <button
          type="button"
          onClick={() => void signOut({ redirectUrl: window.location.origin })}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
        </button>
      </div>
    </aside>
  );
}

function MobileNav() {
  return (
    <nav
      aria-label="Partner portal sections"
      className="flex gap-1 overflow-x-auto border-b border-border px-4 py-2 lg:hidden"
    >
      <NavItems compact />
    </nav>
  );
}

function TopBar({ referralUrl, referralCode, code }: { referralUrl: string; referralCode?: string; code: string }) {
  const { user } = useUser();
  const name = user?.fullName || user?.primaryEmailAddress?.emailAddress || 'Partner';
  const initials = name
    .split(/[\s@]+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/90 px-4 py-3 backdrop-blur sm:px-6 lg:px-8">
      <div className="lg:hidden">
        <img src={zerothLogo} alt="Zeroth" className="h-8 w-8 object-contain" />
      </div>
      <ReferralShareControls referralUrl={referralUrl} referralCode={referralCode} code={code} />
      <ThemeToggle />
      <div className="hidden items-center gap-2.5 md:flex">
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary"
        >
          {initials}
        </span>
        <div className="max-w-[180px] leading-tight">
          <p className="truncate text-sm font-medium">{user?.fullName || 'Partner'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {user?.primaryEmailAddress?.emailAddress}
          </p>
        </div>
      </div>
    </header>
  );
}

function PartnerAuth({ mode }: { mode: 'sign-in' | 'sign-up' }) {
  const { isLoaded, isSignedIn } = useAuth();
  const { data: program } = useProgram();
  const signUp = mode === 'sign-up';
  // Keep return links inside the allowlisted staff routes.
  const [params] = useSearchParams();
  const home = !signUp ? adminReturnPath(params.get('next')) : '/';
  const rates = program?.tiers.map((tier) => tier.rateBasisPoints) ?? [];
  const pitch = program
    ? [
        'Your referral link is ready the moment you sign up',
        `Sellers you refer get ${customerOffer(program)}`,
        `Earn ${percent(Math.min(...rates))} to ${percent(Math.max(...rates))} of every payment, for as long as they subscribe`,
        'Free to join. No Zeroth subscription needed',
      ]
    : ['Your referral link is ready the moment you sign up', 'Free to join. No Zeroth subscription needed'];
  // Zeroth users already signed in on zerothhq.com skip straight to their dashboard.
  if (isLoaded && isSignedIn) return <Navigate to={home} replace />;
  return (
    <div className="grid min-h-[var(--app-viewport-height)] bg-background text-foreground lg:grid-cols-2">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-slate-950 p-10 text-white lg:flex">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(106,90,240,0.45),transparent_60%)]"
        />
        <div className="relative flex items-center gap-2.5">
          <img src={zerothLogo} alt="" className="h-9 w-9 object-contain" />
          <span className="text-lg font-semibold">Zeroth Partners</span>
        </div>
        <div className="relative max-w-md">
          <h2 className="text-4xl font-semibold leading-tight tracking-tight">
            {program ? `Give sellers ${percent(program.customerDiscount.basisPoints)} off.` : 'Refer Amazon sellers.'}
            <br />
            <span className="text-primary-300">
              {program ? `Earn up to ${percent(Math.max(...rates))}, every month.` : 'Earn every month.'}
            </span>
          </h2>
          <ul className="mt-8 space-y-3 text-sm text-slate-300">
            {pitch.map((line) => (
              <li key={line} className="flex gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary-300" aria-hidden="true" />
                {line}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-slate-400">
          Questions? <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </section>
      <main className="flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-6">
          <div className="absolute right-4 top-4">
            <ThemeToggle />
          </div>
          <div className="space-y-2 text-center">
            <div className="flex justify-center lg:hidden">
              <img src={zerothLogo} alt="Zeroth" className="h-10 w-10 object-contain" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {signUp ? 'Become a Zeroth partner' : 'Sign in to your partner portal'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {signUp
                ? 'Create your free account and get your referral link instantly.'
                : 'See your earnings, referrals, tier progress, and payouts.'}
            </p>
          </div>
          {signUp ? (
            <SignUp
              routing="path"
              path="/sign-up"
              signInUrl="/sign-in"
              forceRedirectUrl="/"
              appearance={clerkAppearance}
            />
          ) : (
            <SignIn
              routing="path"
              path="/sign-in"
              signUpUrl="/sign-up"
              forceRedirectUrl={home}
              appearance={clerkAppearance}
            />
          )}
        </div>
      </main>
    </div>
  );
}
